import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import DataTable from '../componentes/DataTable';
import EditModal from '../componentes/EditModal';
import DeleteConfirm from '../componentes/DeleteConfirm';
import Badge from '../componentes/Badge';
import { Plus, Star, Stethoscope, Scissors, Home, HelpCircle } from 'lucide-react';

const ICONOS_TIPO = {
  veterinaria: Stethoscope,
  peluqueria: Scissors,
  guarderia: Home,
};

const TipoTag = ({ tipo }) => {
  const Icon = ICONOS_TIPO[tipo?.toLowerCase()] || HelpCircle;
  return <Badge variant="primary" icon={Icon}>{tipo || 'Otro'}</Badge>;
};

const ContactosPanel = () => {
  const [contactos, setContactos] = useState([]);
  const [mascotas, setMascotas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');

  // Carga inicial: contactos + mascotas + usuarios
  const fetchContactos = async () => {
    try {
      setLoading(true);
      const [contRes, mascRes, usuRes] = await Promise.all([
        api.getContactos(),
        api.getMascotas(),
        api.getUsuarios(),
      ]);
      setContactos(contRes.data?.data || contRes.data || []);
      setMascotas(mascRes.data?.data || mascRes.data || []);
      setUsuarios(usuRes.data?.data || usuRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContactos(); }, []);

  // Mapas para resolver foráneas
  const mascotaMap = useMemo(
    () => Object.fromEntries(mascotas.map((m) => [m.id, m])),
    [mascotas]
  );
  const usuarioMap = useMemo(
    () => Object.fromEntries(usuarios.map((u) => [u.id, u])),
    [usuarios]
  );

  // Filtro de búsqueda: dueño, mascota, nombre del contacto, tipo
  const filtered = useMemo(() => {
    if (!search.trim()) return contactos;
    const term = search.toLowerCase();
    return contactos.filter((c) => {
      const mascota = mascotaMap[c.id_mascota];
      const mascName = mascota?.nombre?.toLowerCase() || '';
      const dueño = usuarioMap[c.usuario_id];
      const dueñoName = dueño
        ? `${dueño.nombre} ${dueño.apellido}`.toLowerCase()
        : '';
      const contactoName = `${c.nombre || ''} ${c.apellido || ''}`.toLowerCase();
      const tipo = c.tipo?.toLowerCase() || '';
      return (
        mascName.includes(term) ||
        dueñoName.includes(term) ||
        contactoName.includes(term) ||
        tipo.includes(term) ||
        String(c.id_mascota).includes(term)
      );
    });
  }, [contactos, search, mascotaMap, usuarioMap]);

  // Handlers
  const handleEdit = (c) => { setSelected({ ...c }); setShowEdit(true); };
  const handleCreate = () => { setSelected(null); setShowEdit(true); };
  const handleDelete = (id) => {
    setSelected(contactos.find((c) => c.id === id));
    setShowDelete(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selected) {
        await api.updateContacto(selected.id, formData);
      } else {
        await api.createContacto(formData);
      }
      setShowEdit(false);
      fetchContactos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteContacto(selected.id);
      setShowDelete(false);
      fetchContactos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      key: 'usuario_id',
      label: 'Dueño',
      id: 'col-dueno',
      render: (_, row) => {
        const dueño = usuarioMap[row.usuario_id];
        return dueño ? `${dueño.nombre} ${dueño.apellido}` : row.usuario_id;
      },
    },
    {
      key: 'id_mascota',
      label: 'Mascota',
      id: 'col-mascota',
      render: (_, row) => {
        const mascota = mascotaMap[row.id_mascota];
        return mascota?.nombre || row.id_mascota;
      },
    },
    { key: 'tipo', label: 'Tipo', id: 'col-tipo', render: (v) => <TipoTag tipo={v} /> },
    { key: 'nombre', label: 'Nombre', id: 'col-nombre' },
    { key: 'apellido', label: 'Apellido', id: 'col-apellido' },
    { key: 'celular', label: 'Celular', id: 'col-celular' },

  ];

  // Opciones para selects del modal
  const tipoOptions = [
    { value: 'veterinaria', label: 'Veterinaria' },
    { value: 'peluqueria', label: 'Peluquería' },
    { value: 'guarderia', label: 'Guardería' },
    { value: 'otro', label: 'Otro' },
  ];

  const mascotaOptions = mascotas.map((m) => ({
    value: m.id,
    label: m.nombre,
  }));

  const usuarioOptions = usuarios.map((u) => ({
    value: u.id,
    label: `${u.nombre} ${u.apellido}`,
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Contactos</h1>
        <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
          <Plus size={18} /> Nuevo
        </button>
      </div>

      {/* Campo de búsqueda */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          className="sp-input"
          type="text"
          placeholder="Buscar por dueño, mascota, nombre, tipo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '380px' }}
        />
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        error={error}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {showEdit && (
        <EditModal
          title={selected ? 'Editar contacto' : 'Crear contacto'}
          fields={[
            { name: 'id_mascota', label: 'Mascota', type: 'select', options: mascotaOptions },
            { name: 'usuario_id', label: 'Dueño', type: 'select', options: usuarioOptions },
            { name: 'tipo', label: 'Tipo de contacto', type: 'select', options: tipoOptions },
            { name: 'categoria_personalizada', label: 'Categoría personalizada' },
            { name: 'nombre', label: 'Nombre' },
            { name: 'apellido', label: 'Apellido' },
            { name: 'celular', label: 'Celular' },
            { name: 'telefono_fijo', label: 'Teléfono fijo' },
            { name: 'direccion', label: 'Dirección' },
            { name: 'horarios', label: 'Horarios' },
            { name: 'dias_atencion', label: 'Días de atención' },
            { name: 'notas', label: 'Notas', type: 'textarea' },
            { name: 'favorito', label: 'Favorito', type: 'checkbox' },
          ]}
          initialValues={selected || {}}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
        />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar el contacto ${selected.nombre}?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default ContactosPanel;